import { FormSectionHandler } from '../entity.handler';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, TimeField, ComboField, CheckListField, CheckBoxField } from '../utils';
import { ICONS } from '../icons';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import * as uuid from 'uuid';
import { AssetStateProfileListItem, PrivacyProfileResponse, PrivacyProfileUpdateRequest, PrivacyScheduleItem, PrivacyStateItem } from '@key-telematics/fleet-api-client';

interface EditorValues {
    name?: string;
    manual?: boolean;
    schedules?: ListFieldValue<PrivacyScheduleItem>[];
    states?: ListFieldValue<PrivacyStateItem>[];
}

const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

export class PrivacyProfileDetailsSectionHandler implements FormSectionHandler<PrivacyProfileResponse, EditorValues> {

    type = 'form';
    icon = ICONS.privacyprofile;
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');
    warning = null;

    stateProfiles: AssetStateProfileListItem[] = [];

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private creating: boolean
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.PRIVACYPROFILE.DETAILS.${key}`, values);
    }

    applyUpdate(entity: PrivacyProfileResponse, payload: PrivacyProfileUpdateRequest): Promise<PrivacyProfileResponse> {
        return this.admin.applyUpdate(api => api.entities.updatePrivacyProfile(entity.id, payload));
    }

    async mapEntityToEditor(entity: PrivacyProfileResponse): Promise<EditorValues> {

        this.stateProfiles = (await this.admin.getCached(`listAssetStateProfiles/${this.ownerId}`, api => api.entities.listAssetStateProfiles(this.ownerId), 15)).items;

        this.warning = entity.scheduleTriggers && entity.scheduleTriggers.length > 0 || entity.stateTriggers && entity.stateTriggers.length > 0 ? null : this.admin.translate(`EDITORS.PRIVACYPROFILE.WARNINGS.ITEM_LENGTH`);
        const result = {
            name: entity.name,
            manual: entity.enableManualTrigger,
            schedules: entity.scheduleTriggers?.map(x => this.scheduleItemToListItem(x)) || [],
            states: entity.stateTriggers?.map(x => this.stateItemToListItem(x)) || [],
        };
        Object.keys(result).forEach(key => result[key] === undefined && delete result[key]); // strip undefined properties from the object
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: PrivacyProfileUpdateRequest, values: EditorValues): Promise<PrivacyProfileUpdateRequest> {
        const patch: PrivacyProfileUpdateRequest = {
            name: values.name,
            scheduleTriggers: values.schedules ? values.schedules.map(x => x.data) : undefined,
            stateTriggers: values.states ? values.states.map(x => x.data) : undefined,
            enableManualTrigger: values.manual,
        };
        Object.keys(patch).forEach(key => patch[key] === undefined && delete patch[key]); // strip undefined properties from the object
        return Promise.resolve(EntityUtils.mergeFields(['name', 'enableManualTrigger', 'scheduleTriggers', 'stateTriggers'], patch, entity));
    }

    getFields(entity: PrivacyProfileResponse): Promise<FormBuilderField[]> {
        return Promise.resolve().then(() => {
            return [
                TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                { ...CheckBoxField('manual', this.translate('FIELDS.ENABLE_MANUAL'), false), description: this.translate('FIELDS.ENABLE_MANUAL_DESC') },
                {
                    id: 'schedules', title: this.translate('FIELDS.SCHEDULES'), type: 'list', required: false, visible: !this.creating,
                    actions: [
                        { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showAddScheduleModal.bind(this), },
                        { id: 'edit', click: this.showAddScheduleModal.bind(this), },
                    ],
                    getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => `${x.name} - (${x.description})`).join('<br/>'),
                },
                {
                    id: 'states', title: this.translate('FIELDS.STATES'), type: 'list', required: false, visible: !this.creating,
                    actions: [
                        { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showAddStateModal.bind(this), },
                        { id: 'edit', click: this.showAddStateModal.bind(this), },
                    ],
                    getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => `${x.name} - (${x.description})`).join('<br/>'),
                },
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, [], fields));
    }

    scheduleItemToListItem(item: PrivacyScheduleItem): ListFieldValue<PrivacyScheduleItem> {
        return {
            id: item.id,
            name: this.translate('FIELDS.FROM_TO', { from: item.from, to: item.to } ),
            icon: 'time',
            description: item.days.sort().map(x => this.admin.i18n.instant(`SHARED.WEEK_DAYS.LONG.${DAYS[x]}`)).join(', '),
            data: item,
        };
    }

    stateItemToListItem(item: PrivacyStateItem): ListFieldValue<PrivacyStateItem> {
        return {
            id: item.id,
            name: this.stateProfiles.find(x => x.id === item.stateProfileId)?.name || 'Unknown',
            icon: ICONS.assetstateprofile,
            description: item.stateValue,
            data: item,
        };
    }

    updateOrAddItem<T extends { id: string }>(items: ListFieldValue<any>[], existingItem: ListFieldValue<any>, curfew: T, itemToListItem: (curfew: T) => ListFieldValue<any>) {
        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = itemToListItem(curfew);
        } else {
            items.push(itemToListItem(curfew));
        }
    }

    async showAddScheduleModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<PrivacyScheduleItem> {

        const input = (item && item.data) || {
            id: uuid.v4(),
            from: '00:00:00',
            to: '23:59:00',
            timeZoneId: this.admin.user.timeZoneId,
            days: ['0', '1', '2', '3', '4', '5', '6'],
        };
        input.days = input.days.map(x => x.toString()); // make sure the days values are strings, as that is required for the check list field

        const timeZones = await this.admin.getTimeZones();
        return this.admin.showFormModal<PrivacyScheduleItem>({
            groups: [{
                name: this.translate('MODALS.SCHEDULE.TITLE'),
                description: this.translate('MODALS.SCHEDULE.DESC'),
                fields: [
                    TimeField('from', this.translate('FIELDS.FROM'), true),
                    TimeField('to', this.translate('FIELDS.TO'), true),
                    ComboField('timeZoneId', this.translate('FIELDS.TIMEZONE'), true, timeZones.map(x => ({ key: x.id, value: x.name }))),
                    CheckListField('days', this.translate('FIELDS.DAYS'), true, DAYS.map((key, index) => ({ key: index.toString(), value: this.admin.i18n.instant(`SHARED.WEEK_DAYS.LONG.${key}`) }))),
                ],
            }],
        }, input).then(time => {
            if (time) {
                this.updateOrAddItem(values.schedules, item, time, this.scheduleItemToListItem.bind(this));
            }
            return time;
        });

    }

    async showAddStateModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<PrivacyScheduleItem> {

        const input = (item && item.data) || {
            id: uuid.v4(),
        };

        return this.admin.showFormModal<PrivacyScheduleItem>({
            groups: [{
                name: this.translate('MODALS.STATE.TITLE'),
                description: this.translate('MODALS.STATE.DESC'),
                fields: [
                    ComboField('stateProfileId', this.translate('FIELDS.STATE_PROFILE'), true, this.stateProfiles.filter(x => x.state === 'active').map(x => ({
                        key: x.id, value: x.name, fields: [
                            ComboField('stateValue', this.translate('FIELDS.STATE_VALUE'), true, x.items.map(y => ({ key:y.state, value: y.state }))),
                        ]
                     })))
                ],
            }],
        }, input).then(time => {
            if (time) {
                this.updateOrAddItem(values.states, item, time, this.stateItemToListItem.bind(this));
            }
            return time;
        });

    }

}
